import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { formatMoney } from '@theme/money-formatting';

/**
 * Updates the cart-drawer reward progress bar from cart subtotal changes.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} bar - The progressbar track.
 * @property {HTMLElement} fill - The filled portion of the track.
 * @property {HTMLElement} markerThreshold - The first reward marker.
 * @property {HTMLElement} markerMax - The max reward marker.
 * @property {HTMLElement} labelThreshold - The $50 reward label.
 * @property {HTMLElement} labelMax - The $75 reward label.
 * @property {HTMLElement} messageFirst - Spend-more copy for the first tier.
 * @property {HTMLElement} amountFirst - Remaining amount for the first tier.
 * @property {HTMLElement} messageMax - Spend-more copy for the max bonus tier.
 * @property {HTMLElement} amountMax - Remaining amount for the max bonus tier.
 * @property {HTMLElement} messageUnlocked - Copy shown once the max tier is reached.
 *
 * @extends {Component<Refs>}
 */
class CartEntriesProgress extends Component {
  requiredRefs = [
    'bar',
    'fill',
    'markerThreshold',
    'markerMax',
    'labelThreshold',
    'labelMax',
    'messageFirst',
    'amountFirst',
    'messageMax',
    'amountMax',
    'messageUnlocked',
  ];

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    document.addEventListener(StandardEvents.cartDiscountUpdate, this.#handleCartUpdate);
    this.#applyFromAttributes();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    document.removeEventListener(StandardEvents.cartDiscountUpdate, this.#handleCartUpdate);
  }

  updatedCallback() {
    super.updatedCallback();
    this.#applyFromAttributes();
  }

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent | import('@shopify/events').CartDiscountUpdateEvent} event
   */
  #handleCartUpdate = (event) => {
    event.promise
      ?.then(({ cart, detail }) => {
        const items = Array.isArray(detail?.items) ? detail.items : undefined;
        const subtotalCents = this.#subtotalCentsFromCart(cart, items);
        if (subtotalCents == null) return;

        this.#setSubtotalCents(subtotalCents);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-entries-progress] Event promise rejected:', error);
      });
  };

  /**
   * @returns {Set<number>}
   */
  #excludedProductIds() {
    const raw = this.dataset.excludeProductIds ?? '';
    /** @type {Set<number>} */
    const ids = new Set();
    for (const part of raw.split(',')) {
      const id = Number(part.trim());
      if (Number.isFinite(id) && id > 0) ids.add(id);
    }
    return ids;
  }

  /**
   * @param {unknown} value
   * @returns {number}
   */
  #numericId(value) {
    if (typeof value === 'number') return value;
    if (typeof value !== 'string') return 0;
    const match = value.match(/(\d+)$/);
    return match ? Number(match[1]) : Number(value);
  }

  /**
   * @param {import('@shopify/events').CartLinesUpdateResult['cart']} cart
   * @param {Array<{ product_id?: number, final_line_price?: number, line_price?: number }> | undefined} [items]
   * @returns {number | null}
   */
  #subtotalCentsFromCart(cart, items) {
    const excluded = this.#excludedProductIds();

    if (Array.isArray(items)) {
      return items.reduce((sum, item) => {
        if (item.product_id != null && excluded.has(Number(item.product_id))) return sum;
        return sum + (item.final_line_price ?? item.line_price ?? 0);
      }, 0);
    }

    if (!cart) return null;

    const lines = cart.lines ?? [];
    if (lines.length > 0) {
      return lines.reduce((sum, line) => {
        const productId = this.#numericId(
          /** @type {{ merchandise?: { product?: { id?: string } } }} */ (line).merchandise?.product?.id
        );
        if (productId && excluded.has(productId)) return sum;
        const amount = Number(line.cost?.totalAmount?.amount);
        return Number.isFinite(amount) ? sum + Math.round(amount * 100) : sum;
      }, 0);
    }

    const amount = Number(cart.cost?.totalAmount?.amount);
    return Number.isFinite(amount) ? Math.round(amount * 100) : null;
  }

  #applyFromAttributes() {
    const subtotalCents = Number(this.dataset.subtotalCents);
    if (!Number.isFinite(subtotalCents)) return;

    this.#setSubtotalCents(subtotalCents);
  }

  /**
   * @param {number} remainingCents
   * @returns {string}
   */
  #formatRemaining(remainingCents) {
    const format = this.dataset.moneyFormat || '${{amount}}';
    const currency = this.dataset.currency || Shopify.currency?.active || 'USD';
    return formatMoney(remainingCents, format, currency);
  }

  /**
   * @param {number} subtotalCents
   */
  #setSubtotalCents(subtotalCents) {
    const maxCents = Number(this.dataset.maxCents);
    const thresholdCents = Number(this.dataset.thresholdCents);
    if (!Number.isFinite(maxCents) || maxCents <= 0 || !Number.isFinite(thresholdCents)) return;
    const progress = Math.min(Math.max(subtotalCents / maxCents, 0), 1) * 100;
    const remainingFirstCents = Math.max(thresholdCents - subtotalCents, 0);
    const remainingMaxCents = Math.max(maxCents - subtotalCents, 0);
    const showFirst = subtotalCents < thresholdCents;
    const showMax = subtotalCents >= thresholdCents && subtotalCents < maxCents;
    const showUnlocked = subtotalCents >= maxCents;

    this.style.setProperty('--progress', `${progress}%`);
    this.dataset.subtotalCents = String(subtotalCents);
    this.refs.bar.setAttribute('aria-valuenow', String(Math.min(subtotalCents, maxCents)));
    this.refs.markerThreshold.classList.toggle('is-reached', subtotalCents >= thresholdCents);
    this.refs.markerMax.classList.toggle('is-reached', subtotalCents >= maxCents);
    this.refs.labelThreshold.classList.toggle('is-reached', subtotalCents >= thresholdCents);
    this.refs.labelMax.classList.toggle('is-reached', subtotalCents >= maxCents);
    this.refs.messageFirst.classList.toggle('hidden', !showFirst);
    this.refs.messageMax.classList.toggle('hidden', !showMax);
    this.refs.messageUnlocked.classList.toggle('hidden', !showUnlocked);

    if (showFirst) {
      this.refs.amountFirst.textContent = this.#formatRemaining(remainingFirstCents);
    } else if (showMax) {
      this.refs.amountMax.textContent = this.#formatRemaining(remainingMaxCents);
    }
  }
}

if (!customElements.get('cart-entries-progress')) {
  customElements.define('cart-entries-progress', CartEntriesProgress);
}
